import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Chip, Divider, Skeleton, Typography } from '@mui/material'
import type { LessonScheduleTemplate } from '@/types'
import { formatDateDMY } from '@/utils/date'

type Props = {
  schedule: LessonScheduleTemplate | null
  loading: boolean
}

export function ScheduleReadonlyPreview({ schedule, loading }: Props) {
  const { t } = useTranslation()

  const weekdayLabels = useMemo(
    () =>
      ({
        1: t('lessons.weekday1', 'Понедельник'),
        2: t('lessons.weekday2', 'Вторник'),
        3: t('lessons.weekday3', 'Среда'),
        4: t('lessons.weekday4', 'Четверг'),
        5: t('lessons.weekday5', 'Пятница'),
        6: t('lessons.weekday6', 'Суббота'),
        7: t('lessons.weekday7', 'Воскресенье'),
      }) as Record<number, string>,
    [t]
  )

  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 1 }} />
  }

  if (!schedule) {
    return (
      <Typography color="text.secondary" variant="body2">
        {t('lessonSchedules.empty', 'Расписание не задано')}
      </Typography>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {[1, 2, 3, 4, 5, 6, 7].map((weekday) => {
        const daySlots = (schedule.slots ?? [])
          .filter((s) => s.weekday === weekday)
          .sort((a, b) => a.start_time.localeCompare(b.start_time))

        if (daySlots.length === 0) return null
        return (
          <Box
            key={weekday}
            sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography color="text.secondary" variant="body2" sx={{ minWidth: 160 }}>
              {weekdayLabels[weekday]}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {daySlots.map((s) => (
                <Chip
                  key={`${s.id}-${s.start_time}`}
                  size="medium"
                  label={`${s.start_time.slice(0, 5)}-${s.end_time.slice(0, 5)}`}
                  sx={{
                    '& .MuiChip-label': {
                      fontSize: '1rem',
                      fontWeight: 600,
                      py: 0.25,
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )
      })}
      {(schedule.slots ?? []).length === 0 && (
        <Typography color="text.secondary">{t('lessonSchedules.noSlots')}</Typography>
      )}

      <Divider sx={{ my: 1 }} />

      <Typography variant="subtitle2" fontWeight={700}>
        {t('lessons.pausesTitle', 'Паузы / отпуска')}
      </Typography>
      {(schedule.pauses ?? []).length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {(schedule.pauses ?? []).map((p) => (
            <Box
              key={p.id}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 0.5,
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {formatDateDMY(p.date_from)} – {formatDateDMY(p.date_to)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                {p.reason?.trim() ? p.reason : '—'}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography color="text.secondary" variant="body2">
          {t('lessonSchedules.noPauses')}
        </Typography>
      )}
    </Box>
  )
}
